import { useEffect, useState, useRef } from 'react';
import { subscribeForAudioLevel } from '../../webrtc/utils';
import throttle from 'lodash.throttle';

const AUDIO_LEVEL_THRESHOLD = 20;
const THROTTLE_WAIT = 100;

/**
 * Provides throttled audio level updates for provided audioTrack.
 *
 * @param audioTrack - audioTrack to be subscribed to
 * @returns {number} - audio level from 0 to 100
 */
export const useAudioLevel = (audioTrack?: MediaStreamTrack): number => {
  const [audioLevel, setAudioLevel] = useState(0);
  const subscribeToLevelPromiseRef = useRef<Promise<() => void> | undefined>(
    undefined,
  );

  // Create a throttled setState function that persists across re-renders
  const throttledSetAudioLevel = useRef(
    throttle((level: number) => {
      setAudioLevel(level);
    }, THROTTLE_WAIT),
  ).current;

  const unsubscribeFromAudioLevel = async (): Promise<void> => {
    if (subscribeToLevelPromiseRef.current !== undefined) {
      try {
        const unsubscribe = await subscribeToLevelPromiseRef.current;
        unsubscribe();
      } catch (error) {
        console.error(error);
      }
    }
  };

  useEffect(() => {
    void unsubscribeFromAudioLevel();

    if (audioTrack) {
      subscribeToLevelPromiseRef.current = subscribeForAudioLevel(
        audioTrack,
        (level) => {
          if (level > AUDIO_LEVEL_THRESHOLD) {
            throttledSetAudioLevel(Math.round(level));
          } else {
            throttledSetAudioLevel(0);
          }
        },
      );
      subscribeToLevelPromiseRef.current.catch((error) => {
        console.error(error);
        throttledSetAudioLevel(0);
      });
    } else {
      throttledSetAudioLevel(0);
    }

    return () => {
      // Cancel any pending throttled updates
      throttledSetAudioLevel.cancel();
      void unsubscribeFromAudioLevel();
    };
  }, [audioTrack, throttledSetAudioLevel]);

  return audioLevel;
};
